import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Building2, Car, Loader2 } from "lucide-react";

import api from "@/lib/api";

const money = (n) =>
  typeof n === "number" && !isNaN(n)
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "—";

export default function AIPicks() {
  const [picks, setPicks] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/ai/picks")
      .then((r) => { if (alive) { setPicks(r.data?.picks || []); setMessage(r.data?.message || ""); } })
      .catch(() => { if (alive) setPicks([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const empty = !loading && (!picks || picks.length === 0);

  return (
    <div data-testid="ai-picks">
      <div className="flex items-center gap-2 font-mono-data text-[10px] uppercase tracking-[0.25em] text-accent mb-5">
        <Sparkles className="w-3.5 h-3.5" /> AI-Picked For You
      </div>
      {loading ? (
        <div className="flex items-center gap-2 font-mono-data text-sm text-muted-foreground animate-pulse py-6" data-testid="ai-picks-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Curating your recommendations…
        </div>
      ) : empty ? (
        <div className="rounded-2xl border border-dashed border-border bg-surface/40 py-10 px-6 text-center" data-testid="ai-picks-empty">
          <Sparkles className="w-6 h-6 text-accent/60 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">{message || "Start browsing to see recommendations."}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {picks.map((p, i) => {
            const to = p.item_type === "vehicle" ? `/vehicles/${p.id}` : `/homes/${p.id}`;
            const Icon = p.item_type === "vehicle" ? Car : Building2;
            return (
              <motion.div key={p.id || i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} whileHover={{ y: -5 }}>
                <Link to={to} data-testid="ai-pick-card"
                  className="block rounded-2xl border border-border bg-surface overflow-hidden group hover:border-accent/50 transition-colors h-full">
                  <div className="relative aspect-[4/3] bg-background/60 overflow-hidden">
                    {p.image ? (
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
                        <Icon className="w-10 h-10" strokeWidth={1.2} />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-background/80 backdrop-blur-md border border-accent/40 px-2.5 py-1 font-mono-data text-[9px] uppercase tracking-wider text-accent">
                      <Sparkles className="w-3 h-3" /> AI Match
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-display text-sm font-medium truncate">{p.title}</div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      {p.subtitle && <span className="text-xs text-muted-foreground truncate">{p.subtitle}</span>}
                      <span className="font-mono-data text-xs text-accent shrink-0">{money(p.price)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2.5 leading-relaxed line-clamp-3" data-testid="ai-pick-reason">{p.reason}</p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
